<template>
    <div class="admin-sessions-page">
        <div class="page-header">
            <h2>SOS Sessions</h2>

            <div class="add-place-box form-wrapper-one">
                <input
                    v-model="searchQuery"
                    type="text"
                    placeholder="Search by sessionId, status, owner, trusted user..."
                    class="form-control"
                />
            </div>
        </div>

        <div v-if="loading" class="state-box">
            Зареждане на sessions...
        </div>

        <div v-else-if="errorMessage" class="state-box error">
            {{ errorMessage }}
        </div>
        

        <div v-else class="places-card">
            <div class="places-card-header">
                <h4>SOS Sessions</h4>
                <span class="places-count">{{ filteredSessions.length }} items</span>
            </div>

            <div v-if="!filteredSessions.length" class="state-box">
                Няма намерени sessions.
            </div>

            <div v-else class="table-wrap">
                <table class="places-table">
                    <thead>
                    <tr>
                        <th>Session</th>
                        <th>Status</th>
                        <th>Owner</th>
                        <th>Trusted</th>
                        <th>Helper</th>
                        <th>Owner Address</th>
                        <th>Owner SOS</th>
                        <th>Help Accepted</th>
                        <th>Created</th>
                        <th>Ended</th>
                        <th>Updated</th>
                        <th>Actions</th>
                    </tr>
                    </thead>

                    <tbody>
                    <tr v-for="session in filteredSessions" :key="session.id">
                        <td>
                            <div class="user-meta">
                                <div class="user-name">{{ session.sessionId || session.id || '-' }}</div>
                                <div class="user-uid">{{ session.id }}</div>
                            </div>
                        </td>

                        <td>
                            <span :class="statusBadgeClass(session.status)">
                                {{ session.status || '-' }}
                            </span>
                        </td>

                        <!-- OWNER -->
                        <td>
                            <div class="user-cell">
                                <div class="photo-cell">
                                    <img
                                        v-if="getUser(session.ownerUid)?.avatarUrl"
                                        :src="getUser(session.ownerUid).avatarUrl"
                                        alt="avatar"
                                        class="place-photo"
                                        @error="onImageError"
                                    />
                                    <span v-else>-</span>
                                </div>

                                <div class="user-meta">
                                    <div class="user-name">
                                        {{ getUserDisplayName(session.ownerUid) }}
                                    </div>
                                    <div class="user-email">
                                        {{ getUser(session.ownerUid)?.email || '-' }}
                                    </div>
                                    <div class="user-uid">
                                        {{ session.ownerUid || '-' }}
                                    </div>
                                </div>
                            </div>
                        </td>

                        <!-- TRUSTED -->
                        <td>
                            <div class="user-cell">
                                <div class="photo-cell">
                                    <img
                                        v-if="getUser(session.trustedUid)?.avatarUrl"
                                        :src="getUser(session.trustedUid).avatarUrl"
                                        alt="avatar"
                                        class="place-photo"
                                        @error="onImageError"
                                    />
                                    <span v-else>-</span>
                                </div>

                                <div class="user-meta">
                                    <div class="user-name">
                                        {{ getUserDisplayName(session.trustedUid) }}
                                    </div>
                                    <div class="user-email">
                                        {{ getUser(session.trustedUid)?.email || '-' }}
                                    </div>
                                    <div class="user-uid">
                                        {{ session.trustedUid || '-' }}
                                    </div>
                                </div>
                            </div>
                        </td>

                        <!-- HELPER -->
                        <td>
                            <div class="user-cell">
                                <div class="photo-cell">
                                    <img
                                        v-if="getUser(session.helperUid)?.avatarUrl"
                                        :src="getUser(session.helperUid).avatarUrl"
                                        alt="avatar"
                                        class="place-photo"
                                        @error="onImageError"
                                    />
                                    <span v-else>-</span>
                                </div>

                                <div class="user-meta">
                                    <div class="user-name">
                                        {{ getUserDisplayName(session.helperUid) }}
                                    </div>
                                    <div class="user-email">
                                        {{ getUser(session.helperUid)?.email || '-' }}
                                    </div>
                                    <div class="user-uid">
                                        {{ session.helperUid || '-' }}
                                    </div>
                                </div>
                            </div>
                        </td>

                        <td class="user-address">
                            {{ session.ownerAddress || '-' }}
                        </td>

                        <td>
                            <span :class="session.ownerSosOn ? 'badge yes' : 'badge no'">
                                {{ session.ownerSosOn ? 'Yes' : 'No' }}
                            </span>
                        </td>

                        <td>
                            <span :class="session.helpAccepted ? 'badge yes' : 'badge no'">
                                {{ session.helpAccepted ? 'Yes' : 'No' }}
                            </span>
                        </td>

                        <td>{{ formatAnyDate(session.createdAt) }}</td>
                        <td>{{ formatAnyDate(session.endedAt) }}</td>
                        <td>{{ formatAnyDate(session.updatedAt) }}</td>

                        <td>
                            <div class="actions-col">
                                <button
                                    class="action-btn delete"
                                    @click="endSession(session)"
                                    :disabled="endingSessionId === session.id || session.status === 'ended'"
                                >
                                    {{
                                        endingSessionId === session.id
                                            ? 'Ending...'
                                            : (session.status === 'ended' ? 'Ended' : 'End session')
                                    }}
                                </button>
                            </div>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
import { collection, getDocs, onSnapshot, query } from "firebase/firestore";
import { getAuthToken, sentrysDb } from "@/firebase";

export default {
    name: "AdminSessions",

    data() {
        return {
            loading: false,
            errorMessage: "",
            sessions: [],
            usersByUid: {},
            searchQuery: "",
            endingSessionId: null,
            sessionsUnsub: null,
        };
    },

    computed: {
        filteredSessions() {
            const q = (this.searchQuery || "").trim().toLowerCase();
            if (!q) return this.sessions;

            return this.sessions.filter((item) => {
                const owner = this.getUser(item.ownerUid);
                const trusted = this.getUser(item.trustedUid);
                const helper = this.getUser(item.helperUid);

                const haystack = [
                    item.sessionId,
                    item.status,
                    item.ownerUid,
                    item.trustedUid,
                    item.helperUid,
                    item.ownerAddress,
                    owner?.firstName,
                    owner?.email,
                    trusted?.firstName,
                    trusted?.email,
                    helper?.firstName,
                    helper?.email,
                ]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();

                return haystack.includes(q);
            });
        }
    },

    async mounted() {
        await this.fetchUsersMap();
        this.subscribeToSessions();
    },

    beforeUnmount() {
        if (this.sessionsUnsub) {
            this.sessionsUnsub();
            this.sessionsUnsub = null;
        }
    },

    methods: {
        async fetchUsersMap() {
            try {
                const usersSnapshot = await getDocs(collection(sentrysDb, "users"));
                const usersMap = {};

                usersSnapshot.docs.forEach((docItem) => {
                    usersMap[docItem.id] = {
                        id: docItem.id,
                        ...docItem.data(),
                    };
                });

                this.usersByUid = usersMap;
            } catch (error) {
                console.error("Fetch users map error:", error);
                this.errorMessage = "Грешка при зареждане на users.";
            }
        },

        subscribeToSessions() {
            this.loading = true;
            this.errorMessage = "";

            const q = query(collection(sentrysDb, "sosSessions"));

            this.sessionsUnsub = onSnapshot(
                q,
                (snapshot) => {
                    this.sessions = snapshot.docs
                        .map((docItem) => ({
                            id: docItem.id,
                            ...docItem.data(),
                        }))
                        .sort((a, b) => {
                            const aTime = this.toMillis(a.updatedAt || a.createdAt);
                            const bTime = this.toMillis(b.updatedAt || b.createdAt);
                            return bTime - aTime;
                        });

                    this.loading = false;
                },
                (error) => {
                    console.error("Live sessions error:", error);
                    this.errorMessage = "Грешка при зареждане на sessions.";
                    this.loading = false;
                }
            );
        },

        getUser(uid) {
            if (!uid) return null;
            return this.usersByUid[uid] || null;
        },

        getUserDisplayName(uid) {
            const user = this.getUser(uid);
            if (!user) return this.shortText(uid);
            return user.firstName || this.shortText(uid);
        },

        async endSession(session) {
            if (!session || !session.id) return;

            const confirmRun = window.confirm(
                `Сигурен ли си, че искаш да прекратиш сесия ${session.sessionId || session.id}?`
            );
            if (!confirmRun) return;

            this.endingSessionId = session.id;

            try {
                const token = await getAuthToken();

                const response = await fetch(
                    "https://us-central1-sentrys-34ab8.cloudfunctions.net/endSosSessionAdmin",
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            sessionId: session.id
                        })
                    }
                );

                const data = await response.json();

                if (!data.ok) {
                    throw new Error(data.error || "Failed to end session");
                }

                alert(`Session ended: ${session.sessionId || session.id}`);
                // Не викаме fetch ръчно — onSnapshot ще обнови таблицата
            } catch (error) {
                console.error("End session error:", error);
                alert(error.message || "Грешка при прекратяване на сесия.");
            } finally {
                this.endingSessionId = null;
            }
        },

        shortText(value, max = 24) {
            if (!value) return "-";
            return value.length > max ? value.slice(0, max) + "..." : value;
        },

        toMillis(value) {
            if (!value) return 0;

            if (typeof value === "number") return value;

            if (value.toDate && typeof value.toDate === "function") {
                return value.toDate().getTime();
            }

            const parsed = new Date(value).getTime();
            return Number.isNaN(parsed) ? 0 : parsed;
        },

        formatAnyDate(value) {
            const millis = this.toMillis(value);
            if (!millis) return "-";

            return new Intl.DateTimeFormat("bg-BG", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
            }).format(new Date(millis));
        },

        statusBadgeClass(status) {
            const normalized = (status || "").toLowerCase();

            if (normalized === "active" || normalized === "assisting") return "badge yes";
            if (normalized === "ended") return "badge no";

            return "badge";
        },

        onImageError(e) {
            e.target.src = "https://via.placeholder.com/60x60?text=User";
        },
    },
};
</script>

<style lang="scss">
@import './scss/styles.scss';
</style>